import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Tabs from '../common/Tabs';
import Input from '../common/Input';
import Button from '../common/Button';
import RolesManagement from './RolesManagement';
import UserProfile from './UserProfile';
import BusinessAuditLogs from './BusinessAuditLogs';
import Branches from './Branches';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import '../../styles/settings.css';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    business_name: '',
    business_email: '',
    business_phone: '',
    business_address: '',
    tax_number: '',
    receipt_header: '',
    receipt_footer: '',
    paper_size: '80mm',
    show_logo: true,
    show_tax: true,
    show_discount: true,
    mpesa_consumer_key: '',
    mpesa_consumer_secret: '',
    mpesa_passkey: '',
    mpesa_shortcode: '',
    mpesa_environment: 'sandbox'
  });
  
  const [testPhone, setTestPhone] = useState('');
  const [testingMpesa, setTestingMpesa] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleTestMpesa = async () => {
    if (!testPhone) {
      toast.error('Please enter a test phone number');
      return;
    }
    
    setTestingMpesa(true);
    try {
      // First save the current form data so the test uses the latest inputs
      await handleSave();
      
      const res = await api.post('/settings.php/test-mpesa', { phone: testPhone });
      toast.success(res.data.message || 'STK Push sent successfully!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send STK Push');
    } finally {
      setTestingMpesa(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // Fetch user's business settings
      // Fetch user's business settings
      // First get the user to find their business_id
      const authRes = await api.get('/auth/me');
      const businessId = authRes.data.user.business_id;
      
      if (businessId) {
        const setRes = await api.get(`/settings.php/business/${businessId}`);
        const { business, settings } = setRes.data;
        
        setFormData({
          business_name: business.business_name || '',
          business_email: business.business_email || '',
          business_phone: business.business_phone || '',
          business_address: business.business_address || '',
          tax_number: business.tax_number || '',
          receipt_header: settings.receipt_header || '',
          receipt_footer: settings.receipt_footer || '',
          paper_size: settings.paper_size || '80mm',
          show_logo: settings.show_logo !== false && settings.show_logo !== 'false',
          show_tax: settings.show_tax !== false && settings.show_tax !== 'false',
          show_discount: settings.show_discount !== false && settings.show_discount !== 'false',
          mpesa_consumer_key: settings.mpesa_config?.consumer_key || '',
          mpesa_consumer_secret: settings.mpesa_config?.consumer_secret || '',
          mpesa_passkey: settings.mpesa_config?.passkey || '',
          mpesa_shortcode: settings.mpesa_config?.shortcode || '',
          mpesa_environment: settings.mpesa_config?.environment || 'sandbox'
        });

        if (business.logo_url) {
          setLogoPreview(`http://localhost${business.logo_url}`);
        }
      }
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formPayload = new FormData();
      Object.keys(formData).forEach(key => {
        if (!key.startsWith('mpesa_')) {
          formPayload.append(key, formData[key]);
        }
      });

      // Add mpesa_config as a JSON string
      formPayload.append('mpesa_config', JSON.stringify({
        consumer_key: formData.mpesa_consumer_key,
        consumer_secret: formData.mpesa_consumer_secret,
        passkey: formData.mpesa_passkey,
        shortcode: formData.mpesa_shortcode,
        environment: formData.mpesa_environment
      }));
      
      if (logoFile) {
        formPayload.append('business_logo', logoFile);
      }

      await api.post('/settings.php', formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General Settings' },
    { id: 'receipt', label: 'Receipt Settings' },
    { id: 'mpesa', label: 'MPesa Integration' },
    { id: 'branches', label: 'Branches' },
    { id: 'profile', label: 'My Profile' },
    { id: 'roles', label: 'Roles & Permissions' },
    { id: 'audit', label: 'Audit Logs' },
    { id: 'backup', label: 'Backup & Restore' }
  ];



  return (
    <div className="settings">
      <div className="settings-header">
        <h1>Settings</h1>
        <Button variant="primary" onClick={handleSave} loading={saving}>
          Save Changes
        </Button>
      </div>

      <Card>
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        
        <div className="settings-content">
          {activeTab === 'general' && (
            <div className="settings-section">
              <h3>Business Information</h3>
              <div className="form-grid">
                <Input label="Business Name" name="business_name" value={formData.business_name} onChange={handleChange} placeholder="Enter business name" />
                <Input label="Business Email" name="business_email" value={formData.business_email} onChange={handleChange} type="email" placeholder="business@example.com" />
                <Input label="Phone Number" name="business_phone" value={formData.business_phone} onChange={handleChange} placeholder="+254 700 000000" />
                <Input label="Address" name="business_address" value={formData.business_address} onChange={handleChange} placeholder="Business address" />
                <Input label="Tax Number (KRA PIN)" name="tax_number" value={formData.tax_number} onChange={handleChange} placeholder="PIN/ VAT number" />
                <Input label="Currency" value="KES" disabled />
              </div>

              <div className="logo-upload-section">
                <label className="input-label">Business Logo</label>
                <div className="logo-preview-container">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Business Logo" className="logo-preview-img" />
                  ) : (
                    <div className="logo-placeholder">No Logo</div>
                  )}
                  <div className="logo-upload-controls">
                    <input type="file" id="logo-upload" accept="image/*" onChange={handleLogoChange} style={{display: 'none'}} />
                    <label htmlFor="logo-upload" className="upload-btn">Upload Logo</label>
                    <span className="upload-hint">Recommended: 200x200px or similar square ratio</span>
                  </div>
                </div>
              </div>

              <h3>System Preferences</h3>
              <div className="form-grid">
                {/* FIX: Use native select instead of Input with type="select" */}
                <div className="input-wrapper">
                  <label className="input-label">Default Language</label>
                  <select className="input-field">
                    <option>English</option>
                    <option>Swahili</option>
                  </select>
                </div>
                
                <div className="input-wrapper">
                  <label className="input-label">Time Zone</label>
                  <select className="input-field">
                    <option>Africa/Nairobi</option>
                  </select>
                </div>
                
                <div className="input-wrapper">
                  <label className="input-label">Date Format</label>
                  <select className="input-field">
                    <option>DD/MM/YYYY</option>
                    <option>MM/DD/YYYY</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'receipt' && (
            <div className="settings-section">
              <h3>Receipt Settings</h3>
              <div className="form-grid">
                <Input label="Receipt Header" name="receipt_header" value={formData.receipt_header} onChange={handleChange} placeholder="Your business name" />
                <Input label="Receipt Footer" name="receipt_footer" value={formData.receipt_footer} onChange={handleChange} placeholder="Thank you for shopping!" />
                
                <div className="input-wrapper">
                  <label className="input-label">Paper Size</label>
                  <select className="input-field" name="paper_size" value={formData.paper_size} onChange={handleChange}>
                    <option value="80mm">80mm (Thermal)</option>
                    <option value="58mm">58mm (Thermal)</option>
                    <option value="A4">A4</option>
                  </select>
                </div>
              </div>
              <div className="checkbox-group">
                <label>
                  <input type="checkbox" name="show_logo" checked={formData.show_logo} onChange={handleChange} /> Show logo on receipt
                </label>
                <label>
                  <input type="checkbox" name="show_tax" checked={formData.show_tax} onChange={handleChange} /> Show tax details
                </label>
                <label>
                  <input type="checkbox" name="show_discount" checked={formData.show_discount} onChange={handleChange} /> Show discount
                </label>
              </div>
            </div>
          )}

          {activeTab === 'mpesa' && (
            <div className="settings-section">
              <h3>M-Pesa API Configuration</h3>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Enter your Safaricom Daraja API credentials to enable Lipa na M-Pesa STK Push.
              </p>
              <div className="form-grid">
                <div className="input-wrapper">
                  <label className="input-label">Environment</label>
                  <select 
                    className="input-field" 
                    name="mpesa_environment"
                    value={formData.mpesa_environment}
                    onChange={handleChange}
                  >
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>
                <Input label="Consumer Key" name="mpesa_consumer_key" value={formData.mpesa_consumer_key} onChange={handleChange} placeholder="Enter Consumer Key" />
                <Input label="Consumer Secret" name="mpesa_consumer_secret" value={formData.mpesa_consumer_secret} onChange={handleChange} placeholder="Enter Consumer Secret" type="password" />
                <Input label="Shortcode (Paybill/Till)" name="mpesa_shortcode" value={formData.mpesa_shortcode} onChange={handleChange} placeholder="e.g. 174379" />
                <Input label="Passkey" name="mpesa_passkey" value={formData.mpesa_passkey} onChange={handleChange} placeholder="Enter Passkey" type="password" />
              </div>
              
              <div style={{ marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                <h4>Test M-Pesa Integration</h4>
                <p style={{ color: '#666', marginBottom: '15px', fontSize: '0.9rem' }}>
                  Send a test STK push (KES 1) to verify your credentials are correct.
                </p>
                <div style={{ display: 'flex', gap: '10px', maxWidth: '400px' }}>
                  <Input 
                    placeholder="Phone (e.g. 0712345678)" 
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                  />
                  <Button 
                    variant="secondary" 
                    onClick={handleTestMpesa} 
                    loading={testingMpesa}
                  >
                    Send Test Push
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'branches' && (
            <div className="settings-section">
              <Branches />
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="settings-section">
              <UserProfile />
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="settings-section">
              <RolesManagement />
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="settings-section">
              <h3>System Audit Logs</h3>
              <p className="text-muted" style={{ marginBottom: '15px' }}>
                View all actions performed by users within your business.
              </p>
              <BusinessAuditLogs />
            </div>
          )}

          {activeTab === 'backup' && (
            <div className="settings-section">
              <h3>Backup & Restore</h3>
              <div className="backup-actions">
                <Button variant="primary" icon="💾">Create Backup Now</Button>
                <Button variant="secondary" icon="📤">Export Data</Button>
                <Button variant="secondary" icon="📥">Import Data</Button>
              </div>
              <div className="backup-history">
                <h4>Recent Backups</h4>
                <p className="text-muted">No backups available</p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Settings;